import { z } from '@tasama/utils';

const validationMessages = {
  uploadedFiles: {
    required: 'Please select a file to upload',
    mustComplete: 'Please wait until the document finishes uploading',
    tooLarge: 'Maximum file size is 5 MB per document',
    invalidSize: 'Invalid file size format',
    invalidType: 'Unsupported file type. Allowed types: PDF, PNG, JPG',
  },
};

export const SCUploadStatusEnum = z.enum(['uploading', 'complete', 'error']);

export const scUploadedFileSchema = z.object({
  id: z.string().min(1),
  fileName: z.string().min(1),
  fileSize: z.string().min(1),
  bytes: z.number().int().min(0).optional(),
  progress: z.number().min(0).max(100),
  status: SCUploadStatusEnum,
  file: z.instanceof(File).optional(),
});

export const scUploadValidations = {
  uploadedFiles: z
    .array(scUploadedFileSchema)
    .length(1, validationMessages.uploadedFiles.required)
    .superRefine((files, ctx) => {
      const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
      const ALLOWED_EXTS = new Set(['pdf', 'png', 'jpg', 'jpeg']);

      // Helper to compute bytes from file entry
      const toBytes = (file: {
        fileSize?: unknown;
        bytes?: unknown;
      }): number | null => {
        if (typeof file.bytes === 'number' && Number.isFinite(file.bytes)) {
          return file.bytes as number;
        }
        const raw =
          typeof file.fileSize === 'string' ? file.fileSize.trim() : '';
        if (!raw) return null;
        const m = raw.match(/^(\d+(?:\.\d+)?)\s*(kb|mb|b|bytes)?$/i);

        if (!m || !m[1]) return null;
        const n = parseFloat(m[1]);
        const unit = (m[2] || 'b').toLowerCase();
        if (Number.isNaN(n)) return null;
        switch (unit) {
          case 'mb':
            return Math.round(n * 1024 * 1024);
          case 'kb':
            return Math.round(n * 1024);
          default:
            return Math.round(n);
        }
      };

      // Validate the single file (always at index 0)
      const file = files[0];
      if (!file) return;

      // Validate Type
      const ext = (file.fileName?.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTS.has(ext)) {
        ctx.addIssue({
          code: 'custom',
          message: validationMessages.uploadedFiles.invalidType,
          path: [0],
        });
      }

      // Validate Size
      const bytes = toBytes(file as any);
      if (bytes === null) {
        ctx.addIssue({
          code: 'custom',
          message: validationMessages.uploadedFiles.invalidSize,
          path: [0],
        });
      } else if (bytes > MAX_BYTES) {
        ctx.addIssue({
          code: 'custom',
          message: validationMessages.uploadedFiles.tooLarge,
          path: [0],
        });
      }
    }),
};

export const scUploadSchema = z.object({
  uploadedFiles: scUploadValidations.uploadedFiles,
});

export type SCUploadFormData = z.infer<typeof scUploadSchema>;
export type SCUploadedFile = z.infer<typeof scUploadedFileSchema>;

export const scUploadDefaultValues: SCUploadFormData = {
  uploadedFiles: [],
};

// Form validation helper
export const validateSCUploadForm = (
  data: unknown
): {
  success: boolean;
  errors?: Record<string, string>;
  data?: SCUploadFormData;
} => {
  try {
    const result = scUploadSchema.parse(data);
    return { success: true, data: result };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.issues.forEach((issue) => {
        const path = issue.path.join('.') || 'uploadedFiles';
        errors[path] = issue.message;
      });
      return { success: false, errors };
    }
    return { success: false, errors: { general: 'Validation failed' } };
  }
};
